// PancakeKeepsake.js
// Standalone keepsake screen — the emotional payoff when a customer's pancake is done.
// Five zones: Hero → Record → Share → Shop → Footer.
// All styling inline.

import { useState, useRef, useEffect } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import TestimonialSubmit from "./TestimonialSubmit";
import { computeDisplayTitle } from "../utils/meditations";
import { ADD_ONS, COMBOS, unfulfilledAddOns } from "../utils/combos";
import { trackShare, trackKeepsakeEmail } from "../utils/analytics";

// ── BRAND ────────────────────────────────────────────────────────────────────
const B = {
  navy:   "#1d52a0",
  blue:   "#1f59db",
  sky:    "#4aa4e1",
  orange: "#f57600",
  gold:   "#fcb31f",
  pink:   "#ef537e",
  cream:  "#ffdaad",
  dark:   "#0d1833",
};

function fmtTime(ms) {
  if (!ms) return null;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

function fmtDate(ts) {
  if (!ts) return null;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

// ── MAIN COMPONENT ──────────────────────────────────────────────────────────
// TODO: RSVP system — promo tokens have unique IDs. Adding an RSVP form on the
// promo landing page that writes {name, email, rsvpAt} to the token doc would
// give Dan an instant guest list per campaign. Query: all promo tokens where
// rsvpAt exists, grouped by promoSerialPrefix = campaign breakdown.

export default function PancakeKeepsake({ order, db, preview = false, isPromo = false, promoToken = null }) {
  const [showFullPhoto, setShowFullPhoto] = useState(false);
  const [showPreservation, setShowPreservation] = useState(false);
  const [showMerch, setShowMerch]         = useState(false);
  const [showTip, setShowTip]             = useState(false);
  const [emailSent, setEmailSent]         = useState(false);
  const [emailSending, setEmailSending]   = useState(false);
  const [emailError, setEmailError]       = useState(null);
  const [keepsakeEmail, setKeepsakeEmail] = useState("");
  const [mailingOptIn, setMailingOptIn]   = useState(false);
  const [merchEmail, setMerchEmail]       = useState("");
  const [merchEmailSaved, setMerchEmailSaved] = useState(false);
  const [merchError, setMerchError]       = useState(null);
  const [mintEmail, setMintEmail]         = useState("");
  const [mintEmailSaved, setMintEmailSaved] = useState(false);
  const [copied, setCopied]               = useState(false);
  const [meditationOpen, setMeditationOpen] = useState(false);

  const shareFileRef = useRef(null);
  const saveRef      = useRef(null);

  const hasCutout = !!order.photo_cutout_url;
  const heroSrc   = (!showFullPhoto && hasCutout) ? order.photo_cutout_url : order.photo_url;
  const name      = computeDisplayTitle(order) || order.subjectName || order.recipientName || "Your Pancake";
  const portalUrl = window.location.href;
  const isLivestream = order.source === "livestream";
  const isReady   = order.state === "ready_to_deliver";
  // "Still at the venue" gate for booth-only offers (souvenir print, combo
  // pickup reminder) — the keepsake page lives forever, the booth doesn't.
  const readyMs = order.readyToDeliverAt?.toMillis?.() ?? order.photographedAt?.toMillis?.() ?? null;
  const stillAtVenue = !!readyMs && Date.now() - readyMs < 12 * 60 * 60 * 1000;
  const comboDue = unfulfilledAddOns(order);

  // Pre-fetch image for Web Share API
  useEffect(() => {
    if (!order.photo_cutout_url && !order.photo_url) return;
    const src = order.photo_cutout_url || order.photo_url;
    fetch(src).then(r => r.blob()).then(blob => {
      shareFileRef.current = new File([blob], "pancake-art.png", { type: blob.type });
    }).catch(() => {});
  }, [order.photo_cutout_url, order.photo_url]);

  // ── ACTIONS ──
  function handleShare() {
    trackShare("keepsake");
    const meditationText = order.meditation?.trim();
    const baseText = meditationText
      ? `"${meditationText}"\n— Doctor Dan, the Pancake Man`
      : order.ai_description;
    const shareText = baseText
      ? `${baseText}\n\nMade by The Joy of Pancakes 🥞`
      : "Check out my Joy of Pancakes pancake art! 🥞";
    const base = { title: "My Joy of Pancakes pancake art", text: shareText, url: portalUrl };
    const file = shareFileRef.current;
    if (navigator.share) {
      if (file && navigator.canShare?.({ files: [file] })) {
        navigator.share({ ...base, files: [file] }).catch(() => {});
      } else {
        navigator.share(base).catch(() => {});
      }
    } else {
      // Desktop fallback — copy link
      handleCopy();
    }
  }

  function handleCopy() {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(portalUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => fallbackCopy());
    } else {
      fallbackCopy();
    }
  }
  function fallbackCopy() {
    const ta = document.createElement("textarea");
    ta.value = portalUrl;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleSendEmail() {
    const trimmed = keepsakeEmail.trim();
    if (!trimmed || emailSending || emailSent) return;
    setEmailSending(true);
    setEmailError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "sendKeepsakeEmail");
      await fn({ orderId: order.id, tokenId: order.tokenId, email: trimmed, mailingList: mailingOptIn });
      setEmailSent(true);
      trackKeepsakeEmail();
    } catch {
      setEmailError("Couldn't send — check your email and try again.");
    }
    setEmailSending(false);
  }

  async function startCheckout(option, addFrame = false) {
    try {
      const fn = httpsCallable(getFunctions(getApp()), "createPreservationCheckout");
      const result = await fn({ orderId: order.id, option, addFrame });
      window.location.href = result.data.checkoutUrl;
    } catch (err) {
      alert("Checkout error: " + (err.code || "") + " " + (err.message || "unknown"));
    }
  }

  async function saveMerchEmail() {
    const trimmed = merchEmail.trim();
    if (!trimmed) return;
    setMerchError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "joinPodWaitlist");
      await fn({ orderId: order.id, tokenId: order.tokenId, email: trimmed });
      setMerchEmailSaved(true);
    } catch (err) {
      setMerchError(err?.message?.replace(/^.+\//, "") || "Couldn't sign up — try again in a minute.");
    }
  }

  async function saveMintEmail() {
    if (!mintEmail.trim()) return;
    try {
      await updateDoc(doc(db, "orders", order.id), { mintInterestEmail: mintEmail.trim() });
      setMintEmailSaved(true);
    } catch { /* ignore */ }
  }

  // ── RENDER ──
  return (
    <div style={{ background: B.navy, minHeight: "100vh", fontFamily: "var(--jop-display)", maxWidth: "480px", margin: "0 auto", boxSizing: "border-box" }}>

      {/* ═══════════════ ZONE 1 — HERO ═══════════════ */}

      {/* Promo ticket discovery banner */}
      {isPromo && (
        <div style={{
          background: "linear-gradient(135deg, rgba(252,179,31,0.15) 0%, rgba(255,218,173,0.1) 100%)",
          borderBottom: `2px solid ${B.gold}`,
          padding: "1rem 1.5rem",
          textAlign: "center",
        }}>
          <div style={{
            color: B.gold, fontWeight: 900,
            fontSize: "1.1rem", letterSpacing: "0.06em",
          }}>✦ You Found a Golden Ticket! ✦</div>
          {order.catalogNumber && (
            <div style={{ color: B.cream, fontSize: "0.78rem", opacity: 0.7, marginTop: "0.3rem" }}>
              Pancake #{order.catalogNumber} · 1 of {promoToken?.catalogNumber ? "a limited series" : "a kind"}
            </div>
          )}
        </div>
      )}

      <div style={{ textAlign: "center", padding: "1rem 0 0.5rem" }}>
        <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "72px" }} />
      </div>

      {isReady && (
        <div style={{ background: "rgba(76,207,130,0.12)", borderBottom: "1px solid rgba(76,207,130,0.4)", padding: "0.65rem 1.5rem", textAlign: "center" }}>
          <span style={{ color: "#4caf50", fontWeight: "bold", fontSize: "0.95rem" }}>🥞 Your pancake is ready — come pick it up!</span>
        </div>
      )}

      <div style={{ width: "100%", aspectRatio: "1/1", background: hasCutout && !showFullPhoto ? "transparent" : "#000", display: "flex", alignItems: "center", justifyContent: "center", padding: hasCutout && !showFullPhoto ? "1rem" : 0, boxSizing: "border-box" }}>
        <img
          src={heroSrc}
          alt={`Pancake art: ${name}`}
          style={{
            maxWidth: "100%", maxHeight: "100%", display: "block",
            width: hasCutout && !showFullPhoto ? "auto" : "100%",
            height: hasCutout && !showFullPhoto ? "auto" : "100%",
            objectFit: hasCutout && !showFullPhoto ? "contain" : "cover",
            filter: hasCutout && !showFullPhoto ? "drop-shadow(0 18px 36px rgba(0,0,0,0.6)) drop-shadow(0 4px 10px rgba(0,0,0,0.35))" : "none",
          }}
        />
      </div>

      {hasCutout && (
        <div style={{ textAlign: "center", padding: "0.5rem 0" }}>
          <button onClick={() => setShowFullPhoto(v => !v)} style={{ background: "transparent", border: `1px solid rgba(74,164,225,0.5)`, color: B.sky, borderRadius: "8px", padding: "0.3rem 0.85rem", fontSize: "0.75rem", cursor: "pointer" }}>
            {showFullPhoto ? "Show Cutout" : "Show Full Photo"}
          </button>
        </div>
      )}

      {/* Hero info */}
      <div style={{ padding: "1rem 1.5rem 0.5rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.6rem" }}>
          <h1 style={{ color: "white", fontSize: "1.5rem", fontWeight: "bold", margin: "0" }}>{name}</h1>
          {order.catalogNumber && (
            <span style={{ color: B.gold, fontSize: "0.78rem", fontWeight: 700, opacity: 0.7 }}>
              #{order.catalogNumber}
            </span>
          )}
        </div>
        {order.designLabel && <div style={{ color: B.gold, fontSize: "0.82rem", fontWeight: "600", marginTop: "0.15rem" }}>{order.designLabel}</div>}

        {/* Event context — shift title + optional Maps link to the venue.
            venueAddress renders as a Maps link only when venueIsPublic !== false.
            Private events (wedding, corporate) keep the title visible but hide
            the address.                                                    */}
        {order.eventName && (() => {
          const showVenue = order.venueIsPublic !== false && !!order.venueAddress;
          return (
            <div style={{ marginTop: "0.45rem", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.45rem", color: "rgba(255,218,173,0.72)", fontSize: "0.8rem" }}>
              <span style={{ opacity: 0.6 }}>made at</span>
              {showVenue ? (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(order.venueAddress)}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: B.sky, textDecoration: "none", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
                >
                  📍 {order.eventName}
                </a>
              ) : (
                <span style={{ color: B.cream, fontWeight: 700 }}>{order.eventName}</span>
              )}
            </div>
          );
        })()}

        {order.readyToDeliverAt && <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem", marginTop: "0.3rem" }}>{fmtDate(order.readyToDeliverAt)}</div>}
      </div>

      {/* Vibes — artist's reflection tags, floated above meditation */}
      {order.artistTagsReflection?.length > 0 && (
        <div style={{ padding: "0.25rem 1.5rem 0", display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
          {order.artistTagsReflection.map(id => {
            const TAGS = { fire: "🔥", clean: "🎯", magic: "✨", lab: "🧪", grind: "💪", rough: "🫠", cute: "🥰", funny: "😂" };
            const LABELS = { fire: "Fire", clean: "Clean", magic: "Magic", lab: "Lab", grind: "Grind", rough: "Rough", cute: "Cute", funny: "Funny" };
            return (
              <span key={id} style={{ background: "rgba(252,179,31,0.12)", border: "1px solid rgba(252,179,31,0.35)", borderRadius: "999px", padding: "0.2rem 0.65rem", fontSize: "0.78rem", color: B.gold, fontWeight: 600 }}>
                {TAGS[id] || ""} {LABELS[id] || id}
              </span>
            );
          })}
        </div>
      )}

      {/* Meditation — Dan's reflection on this piece */}
      {order.meditation?.trim() && (
        <Meditation text={order.meditation.trim()} open={meditationOpen} onToggle={() => setMeditationOpen((o) => !o)} />
      )}

      {/* ═══════════════ TELL US — gifted opportunity, placed at peak emotion ═══════════════ */}
      {/* The meditation is a gift TO them; the testimonial is a place to give
          a few words back if any come. Lives directly after the meditation so
          it catches the emotional moment, before "Keep It" shifts the user into
          save/share mode. Hidden on promo tickets (random passers-by, no
          emotional connection) and previews. Skippable in every way — the
          page below works whether they fill it or not.                       */}
      {!preview && !isPromo && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          <TestimonialSubmit order={order} />
        </div>
      )}

      {/* ═══════════════ ZONE 1 — KEEP IT ═══════════════ */}
      {/* Primary action: email it to yourself. Secondary: share. Tip is a
          small inline pill, not a top-billed action.                     */}
      {!preview && (
        <div ref={saveRef} style={{ padding: "0.75rem 1.5rem 1.5rem", display: "flex", flexDirection: "column", gap: "0.85rem" }}>
          {emailSent ? (
            <div style={{ background: "rgba(76,207,130,0.12)", border: "1px solid rgba(76,207,130,0.4)", borderRadius: "12px", padding: "0.95rem", textAlign: "center", color: "#4ccf82", fontSize: "0.92rem", fontWeight: "bold" }}>
              ✓ Sent! Check your inbox.
            </div>
          ) : (
            <div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", marginBottom: "0.45rem", fontWeight: 600 }}>
                Email this to yourself
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input type="email" value={keepsakeEmail} onChange={e => { setKeepsakeEmail(e.target.value); setEmailError(null); }}
                  placeholder="your@email.com"
                  style={{ flex: 1, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", color: "white", fontSize: "0.95rem", padding: "0.7rem 0.85rem", outline: "none" }}
                />
                <button onClick={handleSendEmail} disabled={!keepsakeEmail.trim() || emailSending}
                  style={{ background: keepsakeEmail.trim() && !emailSending ? B.pink : "rgba(255,255,255,0.1)", color: keepsakeEmail.trim() ? "white" : "rgba(255,255,255,0.3)", border: "none", borderRadius: "10px", padding: "0.7rem 1.1rem", fontSize: "0.9rem", fontWeight: "bold", cursor: keepsakeEmail.trim() ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
                  {emailSending ? "…" : "Send 🥞"}
                </button>
              </div>
              {emailError && <div style={{ color: "#f87171", fontSize: "0.78rem", marginTop: "0.35rem" }}>{emailError}</div>}
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.55rem", cursor: "pointer" }}>
                <input type="checkbox" checked={mailingOptIn} onChange={e => setMailingOptIn(e.target.checked)} style={{ accentColor: B.gold, width: "16px", height: "16px", flexShrink: 0 }} />
                <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.78rem" }}>Keep me posted on Joy of Pancakes events</span>
              </label>
            </div>
          )}

          {/* Share + Tip row — secondary actions, quieter than email */}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={handleShare} style={{
              flex: 2, background: "rgba(74,164,225,0.12)", border: "1px solid rgba(74,164,225,0.35)",
              borderRadius: "999px", padding: "0.6rem", cursor: "pointer",
              color: B.sky, fontSize: "0.88rem", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
            }}>
              📤 Share
            </button>
            <button onClick={() => setShowTip(true)} style={{
              flex: 1, background: "rgba(252,179,31,0.1)", border: "1px solid rgba(252,179,31,0.35)",
              borderRadius: "999px", padding: "0.6rem", cursor: "pointer",
              color: B.gold, fontSize: "0.86rem", fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center", gap: "0.3rem",
            }}>
              🫙 Tip
            </button>
          </div>
          {!navigator.share && (
            <button onClick={handleCopy} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", padding: "0.5rem", color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>
              {copied ? "✓ Copied!" : "Copy link"}
            </button>
          )}
          <a href={`/gallery/${order.id}`} style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,218,173,0.2)",
            borderRadius: "999px", padding: "0.6rem", textDecoration: "none",
            color: "var(--jop-cream, #ffdaad)", fontSize: "0.85rem", fontWeight: 600,
          }}>
            🖼 See in Gallery
          </a>
        </div>
      )}

      {/* ═══════════════ THE RECORD — context for the piece ═══════════════ */}
      {/* Vibes tags moved above meditation (floated, no label). AI tags and
          physical measurements removed until we surface them with purpose. */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>

        {/* AI Description */}
        {order.ai_description && (
          <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.5rem" }}>About this pancake</div>
            <p style={{ color: B.cream, fontSize: "0.88rem", lineHeight: 1.65, margin: 0 }}>{order.ai_description}</p>
          </div>
        )}

        {/* Stats — time + type + source only. No size / weight — those were
            unreliable from vision-only inference. */}
        <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
          {[
            { label: "Draw time", value: fmtTime(order.drawingTimeMs) },
            { label: "Cook time", value: fmtTime(order.cookTimeMs) },
            { label: "Type", value: order.orderType === "custom" ? "Custom" : order.orderType === "freestyle_flash" ? "Freestyle" : "Flash" },
            { label: "Source", value: order.source === "livestream" ? "📺 Livestream" : "🥞 At the booth" },
          ].filter(s => s.value).map(s => (
            <div key={s.label}>
              <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
              <div style={{ color: "white", fontSize: "0.88rem", fontWeight: "600", marginTop: "2px" }}>{s.value}</div>
            </div>
          ))}
          <div style={{ gridColumn: "1 / -1", textAlign: "right", paddingTop: "0.2rem" }}>
            <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.6rem", fontFamily: "monospace", letterSpacing: "0.05em" }}>
              GTS-{order.id?.slice(-6).toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {!preview && <>
      {/* ═══════════════ ZONE 3 — WANT MORE? ═══════════════ */}
      {/* Commerce + future: preservation, merch, booking. One section,
          consistent treatment, no single item screaming louder than another. */}
      <div style={{ padding: "1.5rem", borderTop: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}>
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700, marginBottom: "0.85rem" }}>
          Want more?
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
          {/* Preserve button: livestream orders only — those guests never
              received the pancake in person, so Preserve & Ship ($65) is
              their delivery path. Booth guests took the pancake home; we
              don't ship kits or accept post-event preservation service. */}
          {isLivestream && (
            <button onClick={() => setShowPreservation(true)} style={{ width: "100%", background: "rgba(252,179,31,0.12)", color: B.gold, border: `1px solid rgba(252,179,31,0.35)`, borderRadius: "12px", padding: "0.85rem", fontSize: "0.92rem", fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
              🥞 Preserve & ship this pancake →
            </button>
          )}
          <button onClick={() => setShowMerch(true)} style={{ width: "100%", background: "rgba(245,118,0,0.1)", color: B.orange, border: `1px solid rgba(245,118,0,0.35)`, borderRadius: "12px", padding: "0.85rem", fontSize: "0.92rem", fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
            🎨 Turn it into merch →
          </button>
          {/* Souvenir print — booth-only offer, so it self-hides ~12h after
              the photo. The hiprint prints THIS photo; nothing to pre-stock. */}
          {stillAtVenue && order.joinVoice !== "free" && (
            <div style={{ width: "100%", boxSizing: "border-box", background: "rgba(74,164,225,0.1)", color: B.sky, border: `1px solid rgba(74,164,225,0.35)`, borderRadius: "12px", padding: "0.85rem", fontSize: "0.92rem", fontWeight: 700, textAlign: "left" }}>
              🖼️ Still at the booth? Take this photo home on paper — $10 souvenir print, just ask.
            </div>
          )}
          <a href="/quote"
            style={{ display: "block", background: "rgba(239,83,126,0.1)", color: B.pink, border: `1px solid rgba(239,83,126,0.35)`, borderRadius: "12px", padding: "0.85rem", fontSize: "0.92rem", fontWeight: 700, textDecoration: "none", textAlign: "left" }}>
            🎪 Book us for your event →
          </a>
        </div>

        {/* Combo pickup reminder — unfulfilled goods riding this order */}
        {comboDue.length > 0 && stillAtVenue && (
          <div style={{ marginTop: "0.85rem", padding: "0.85rem", background: "rgba(252,179,31,0.12)", border: `1px solid rgba(252,179,31,0.4)`, borderRadius: "12px" }}>
            <div style={{ color: B.gold, fontSize: "0.9rem", fontWeight: 700 }}>
              {COMBOS[order.comboKey] ? `${COMBOS[order.comboKey].emoji} ${COMBOS[order.comboKey].label}` : "🎁 Combo"} — don't leave without:
            </div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem", marginTop: "0.3rem" }}>
              {comboDue.map((a) => ADD_ONS[a.type] ? `${ADD_ONS[a.type].emoji} ${ADD_ONS[a.type].label}` : a.type).join(" · ")}
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════ PROMO: About + Find Your Own ═══════════════ */}
      {isPromo && !preview && (
        <div style={{ padding: "1.5rem", background: "linear-gradient(180deg, rgba(31,89,219,0.08) 0%, rgba(0,0,0,0) 100%)", borderTop: "1px solid rgba(252,179,31,0.15)" }}>

          <div style={{ color: B.gold, fontWeight: 900, fontSize: "1rem", marginBottom: "0.6rem" }}>
            About the Artist
          </div>
          <p style={{ color: B.cream, fontSize: "0.88rem", lineHeight: 1.6, margin: "0 0 1rem", opacity: 0.8 }}>
            Doctor Dan the Pancake Man has been illustrating with batter for 17 years — drawing live portraits, characters, and custom designs on a hot griddle in front of audiences around the country. Every pancake is a one-of-a-kind original, drawn freehand with squeeze bottles of colored batter.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <a href="https://twitch.tv/thejoyofpancakes" target="_blank" rel="noopener noreferrer"
              style={{ display: "block", textAlign: "center", background: "rgba(74,164,225,0.15)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", padding: "0.7rem", color: B.sky, fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" }}>
              📺 Watch Live on Twitch
            </a>
            <a href="https://instagram.com/thejoyofpancakes" target="_blank" rel="noopener noreferrer"
              style={{ display: "block", textAlign: "center", background: "rgba(239,83,126,0.12)", border: "1px solid rgba(239,83,126,0.3)", borderRadius: "10px", padding: "0.7rem", color: B.pink, fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" }}>
              📸 Follow on Instagram
            </a>
          </div>
          <div style={{ marginTop: "1rem", textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: "0.75rem" }}>
            This is 1 of a limited series of Golden Tickets hidden in the wild.
          </div>
        </div>
      )}

      {/* Mint tombstone — demoted to a single quiet line */}
      <div style={{ padding: "1.2rem 1.5rem", textAlign: "center", color: "rgba(255,255,255,0.28)", fontSize: "0.72rem", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        {mintEmailSaved ? (
          <span>✓ We'll let you know when minting is live.</span>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); saveMintEmail(); }} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap", justifyContent: "center" }}>
            <span>Digital collectible minting coming soon.</span>
            <input type="email" value={mintEmail} onChange={e => setMintEmail(e.target.value)} placeholder="notify me" aria-label="Email to notify"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "6px", color: "rgba(255,255,255,0.6)", fontSize: "0.72rem", padding: "0.3rem 0.5rem", outline: "none", width: "140px" }} />
            <button type="submit" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", padding: "0.3rem 0.55rem", cursor: "pointer" }}>Save</button>
          </form>
        )}
      </div>
      </>}

      {/* ═══════════════ MODALS — never render in preview mode ═══════════════ */}
      {!preview && <>

      {/* Tip Modal */}
      {showTip && (
        <div onClick={() => setShowTip(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: B.navy, borderRadius: "20px 20px 0 0", padding: "1.5rem", width: "100%", maxWidth: "480px", animation: "keepsakeSlideUp 0.3s ease" }}>
            <style>{`@keyframes keepsakeSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.25rem" }}>🫙</div>
              <div style={{ color: B.cream, fontSize: "1rem", fontWeight: "bold" }}>Tips go straight to your artist</div>
            </div>
            <div style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem" }}>
              <a href="https://venmo.com/thejoyofpancakes" target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: "block", textAlign: "center", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "12px", padding: "0.85rem", color: "white", fontSize: "0.95rem", fontWeight: "600", textDecoration: "none" }}>Venmo</a>
              <a href="https://cash.app/$thejoyofpancakes" target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: "block", textAlign: "center", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "12px", padding: "0.85rem", color: "white", fontSize: "0.95rem", fontWeight: "600", textDecoration: "none" }}>Cash App</a>
            </div>
            <button onClick={() => setShowTip(false)} style={{ width: "100%", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "10px", padding: "0.65rem", color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}

      {/* Preservation Modal */}
      {showPreservation && (
        <div onClick={() => setShowPreservation(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: B.navy, borderRadius: "20px 20px 0 0", padding: "1.5rem", width: "100%", maxWidth: "480px", maxHeight: "80vh", overflowY: "auto", animation: "keepsakeSlideUp 0.3s ease" }}>
            <div style={{ color: B.gold, fontSize: "1.1rem", fontWeight: "bold", marginBottom: "1rem", textAlign: "center" }}>Preserve your pancake forever</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginBottom: "1.25rem" }}>
              {(isLivestream ? [
                { key: "livestream_ship", name: "Preserve & Ship", price: "$65", desc: "We preserve your pancake with decoupage, frame it, and ship it to your door." },
              ] : [
                { key: "kit", name: "Preservation Kit", price: "$15", desc: "Everything you need to preserve your pancake at home. Add a display frame for $5.", addFrame: true },
                { key: "full_service", name: "Full Service Preservation", price: "$125 + shipping", desc: "We handle everything — preservation, framing, and shipping. Ready to hang." },
              ]).map(opt => (
                <div key={opt.key} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(74,164,225,0.2)", borderRadius: "12px", padding: "1rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ color: "white", fontWeight: "bold", fontSize: "0.95rem" }}>{opt.name}</span>
                    <span style={{ color: B.gold, fontWeight: "bold", fontSize: "0.9rem" }}>{opt.price}</span>
                  </div>
                  <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", lineHeight: 1.5, margin: "0.4rem 0 0.75rem" }}>{opt.desc}</p>
                  <button onClick={() => startCheckout(opt.key, false)} style={{ width: "100%", background: B.pink, color: "white", border: "none", borderRadius: "10px", padding: "0.7rem", fontSize: "0.88rem", fontWeight: "bold", cursor: "pointer" }}>
                    Continue
                  </button>
                  {opt.addFrame && (
                    <button onClick={() => startCheckout(opt.key, true)} style={{ width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "10px", padding: "0.6rem", fontSize: "0.82rem", color: "rgba(255,255,255,0.6)", cursor: "pointer", marginTop: "0.4rem" }}>
                      + Add display frame ($5)
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => setShowPreservation(false)} style={{ width: "100%", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "10px", padding: "0.65rem", color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}

      {/* Merch Modal */}
      {showMerch && (
        <div onClick={() => setShowMerch(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: B.navy, borderRadius: "20px 20px 0 0", padding: "1.5rem", width: "100%", maxWidth: "480px", animation: "keepsakeSlideUp 0.3s ease" }}>
            {/* TODO: Printful integration */}
            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.25rem" }}>🎨</div>
              <div style={{ color: B.cream, fontSize: "1rem", fontWeight: "bold" }}>Pancake merch is coming soon</div>
              <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", lineHeight: 1.5, margin: "0.5rem 0 0" }}>
                Sticker packs, totes, and shirts — made from your pancake art.
              </p>
            </div>
            {merchEmailSaved ? (
              <div style={{ background: "rgba(76,207,130,0.12)", border: "1px solid rgba(76,207,130,0.3)", borderRadius: "10px", padding: "0.75rem", textAlign: "center", color: "#4ccf82", fontSize: "0.85rem", fontWeight: "bold", marginBottom: "1rem" }}>
                ✓ We'll notify you!
              </div>
            ) : (
              <>
                <div style={{ display: "flex", gap: "0.5rem", marginBottom: merchError ? "0.4rem" : "1rem" }}>
                  <input type="email" value={merchEmail} onChange={e => { setMerchEmail(e.target.value); setMerchError(null); }} placeholder="your@email.com"
                    style={{ flex: 1, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "8px", color: "white", fontSize: "0.85rem", padding: "0.6rem", outline: "none" }} />
                  <button onClick={saveMerchEmail} disabled={!merchEmail.trim()} style={{ background: merchEmail.trim() ? B.gold : "rgba(255,255,255,0.1)", color: merchEmail.trim() ? B.dark : "rgba(255,255,255,0.3)", border: "none", borderRadius: "8px", padding: "0.6rem 0.85rem", fontWeight: "bold", fontSize: "0.82rem", cursor: merchEmail.trim() ? "pointer" : "not-allowed" }}>Notify me</button>
                </div>
                {merchError && (
                  <div style={{ color: "#ff8a8a", fontSize: "0.78rem", marginBottom: "0.8rem", lineHeight: 1.4 }}>{merchError}</div>
                )}
              </>
            )}
            <button onClick={() => setShowMerch(false)} style={{ width: "100%", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "10px", padding: "0.65rem", color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

// ── MEDITATION BLOCK ────────────────────────────────────────────────────────
// Dan's personal thoughts on THIS specific pancake. No container, no fade —
// just typography. Long meditations collapse cleanly to an ellipsis preview
// with a "Read meditation →" chip.
function Meditation({ text, open, onToggle }) {
  const PREVIEW_LIMIT = 220;
  const isLong = text.length > PREVIEW_LIMIT;
  const showFull = open || !isLong;
  const preview = isLong ? text.slice(0, PREVIEW_LIMIT).replace(/\s+\S*$/, "") + "…" : text;

  return (
    <div style={{ padding: "0.9rem 1.5rem 1rem" }}>
      <div style={{
        color: B.gold,
        fontSize: "0.68rem",
        textTransform: "uppercase",
        letterSpacing: "0.11em",
        marginBottom: "0.55rem",
        fontWeight: 700,
      }}>
        🥞 Dan's thoughts on this pancake
      </div>
      <p style={{
        color: B.cream,
        fontSize: "0.98rem",
        lineHeight: 1.6,
        margin: 0,
        fontStyle: "italic",
        whiteSpace: "pre-wrap",
      }}>
        {showFull ? text : preview}
      </p>
      {isLong && (
        <button
          onClick={onToggle}
          style={{
            marginTop: "0.7rem",
            background: "transparent",
            color: B.gold,
            border: `1px solid ${B.gold}55`,
            borderRadius: "999px",
            padding: "0.32rem 0.85rem",
            fontSize: "0.78rem", fontWeight: 600,
            cursor: "pointer",
            letterSpacing: "0.02em",
          }}
        >
          {open ? "Show less" : "Read meditation →"}
        </button>
      )}
    </div>
  );
}
